import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { Card } from 'react-bootstrap';
import { Tabs, Tooltip, Badge } from 'antd';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import TasksTab from './tabs/TasksTab';
import VISXTimeline from './timeline/VISXTimeline';
import StatusTab from './tabs/StatusTab';
import CommsTab from './tabs/CommsTab';
import CrisisMapTab from './tabs/CrisisMapTab';
import IncidentDocumentationTab from './tabs/IncidentDocumentationTab';
import RegulatoryComplianceTab from './tabs/RegulatoryComplianceTab';
import LessonsLearnedTab from './tabs/LessonsLearnedTab';
import MediaIntelligenceTab from './MediaIntelligenceTab';

const IncidentDetailsTabs = ({ incidentId, incident }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [activeTab, setActiveTab] = useState('status');
  const [notificationCounts, setNotificationCounts] = useState({
    tasks: 0,
    timeline: 0,
    comms: 0,
    'ai-snapshot': 0,
    'regulatory-compliance': 0,
    'media-intelligence': 0
  });






  // Real-time notification counting
  useEffect(() => {
    if (!incidentId || !currentUser?.orgId || !db) return;

    const unsubscribers = [];

    // Count overdue tasks
    const tasksRef = collection(db, 'c3_tasks');
    const tasksQuery = query(
      tasksRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', currentUser.orgId)
    );

    const tasksUnsubscribe = onSnapshot(tasksQuery, (snapshot) => {
      const now = new Date();
      const overdueTasks = snapshot.docs.filter(doc => {
        const task = doc.data();
        if (!task.dueDate || task.status === 'complete') return false;
        const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
        return dueDate < now;
      });

      setNotificationCounts(prev => ({
        ...prev,
        tasks: overdueTasks.length
      }));
    });

    unsubscribers.push(tasksUnsubscribe);

    // Count overdue milestones for timeline
    const milestonesRef = collection(db, 'c3_milestones');
    const milestonesQuery = query(
      milestonesRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', currentUser.orgId)
    );

    const milestonesUnsubscribe = onSnapshot(milestonesQuery, (snapshot) => {
      const now = new Date();
      const overdueMilestones = snapshot.docs.filter(doc => {
        const milestone = doc.data();
        if (!milestone.dueDate || milestone.status === 'Complete') return false;
        const dueDate = milestone.dueDate.toDate ? milestone.dueDate.toDate() : new Date(milestone.dueDate);
        return dueDate < now;
      });

      setNotificationCounts(prev => ({
        ...prev,
        timeline: overdueMilestones.length
      }));
    });

    unsubscribers.push(milestonesUnsubscribe);

    // Count scheduled/pending communications
    const messagesRef = collection(db, 'c3_messages');
    const messagesQuery = query(
      messagesRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', currentUser.orgId)
    );

    const messagesUnsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const pendingMessages = snapshot.docs.filter(doc => {
        const message = doc.data();
        return message.status === 'scheduled' || message.status === 'pending';
      });

      setNotificationCounts(prev => ({
        ...prev,
        comms: pendingMessages.length
      }));
    });

    unsubscribers.push(messagesUnsubscribe);

    // Count AI alerts/recommendations
    const aiSnapshotsRef = collection(db, 'c3_ai_snapshots');
    const aiSnapshotsQuery = query(
      aiSnapshotsRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', currentUser.orgId)
    );

    const aiUnsubscribe = onSnapshot(aiSnapshotsQuery, (snapshot) => {
      let alertCount = 0;
      snapshot.docs.forEach(doc => {
        const snapshot = doc.data();
        if (snapshot.predictiveAlerts) {
          alertCount += snapshot.predictiveAlerts.filter(alert => 
            alert.priority === 'critical' || alert.priority === 'high'
          ).length;
        }
      });

      setNotificationCounts(prev => ({
        ...prev,
        'ai-snapshot': alertCount
      }));
    });

    unsubscribers.push(aiUnsubscribe);

    // Count high-relevance media articles
    const mediaArticlesRef = collection(db, 'c3_media_articles');
    const mediaQuery = query(
      mediaArticlesRef,
      where('orgId', '==', currentUser.orgId)
    );

    const mediaUnsubscribe = onSnapshot(mediaQuery, (snapshot) => {
      const now = new Date();
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      const highRelevanceArticles = snapshot.docs.filter(doc => {
        const article = doc.data();
        const createdAt = article.createdAt ? 
          (article.createdAt.toDate ? article.createdAt.toDate() : new Date(article.createdAt)) : 
          new Date();
        
        return (
          article.relevanceScore >= 0.95 && // High relevance threshold
          createdAt >= last24Hours && // Created in last 24 hours
          !article.acknowledgedBy?.includes(currentUser.id) // Not yet acknowledged by current user
        );
      });

      setNotificationCounts(prev => ({
        ...prev,
        'media-intelligence': highRelevanceArticles.length
      }));
    });

    unsubscribers.push(mediaUnsubscribe);

    // Count regulatory compliance issues
    if (incident?.crisisType) {
      // Simple heuristic: if incident is over 24 hours old and not resolved, flag regulatory compliance
      const incidentAge = incident.createdAt ? 
        (new Date() - incident.createdAt.toDate()) / (1000 * 60 * 60) : 0;
      
      const hasRegulatoryIssues = incidentAge > 24 && incident.status !== 'Resolved';
      
      setNotificationCounts(prev => ({
        ...prev,
        'regulatory-compliance': hasRegulatoryIssues ? 1 : 0
      }));
    }

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [incidentId, currentUser?.orgId, db, incident]);

  // Memoize tabs array to prevent recreation on every render
  const tabs = useMemo(() => {
    const baseTabs = [
    {
      id: 'status',
      label: 'Crisis Summary',
      component: <StatusTab incidentId={incidentId} />,
      icon: '📊',
      tooltip: 'Executive crisis overview with AI intelligence, real-time status and staff welfare'
    },
    {
      id: 'crisis-map',
      label: 'Crisis Map',
      component: <CrisisMapTab incidentId={incidentId} incident={incident} />,
      icon: '🗺️',
      tooltip: 'Interactive crisis map showing location, affected sites, and mass communication tools for rapid response coordination'
    },
    {
      id: 'tasks',
      label: 'Tasks',
      component: <TasksTab incidentId={incidentId} />,
      icon: '📋',
      tooltip: 'View and manage tasks associated with this incident',
      notificationCount: notificationCounts.tasks
    },
    {
      id: 'timeline',
      label: 'Timeline',
      component: <VISXTimeline incidentId={incidentId} incident={incident} />,
      icon: '⏱️',
      tooltip: 'View chronological sequence of events and updates',
      notificationCount: notificationCounts.timeline
    },
    {
      id: 'comms',
      label: 'Comms',
      component: <CommsTab incidentId={incidentId} />,
      icon: '💬',
      tooltip: 'Manage internal and external communications',
      notificationCount: notificationCounts.comms
    },
    {
      id: 'media-intelligence',
      label: 'Media Intelligence',
      component: <MediaIntelligenceTab incidentId={incidentId} incident={incident} />,
      icon: '📺',
      tooltip: 'Monitor real-time media coverage and news analysis related to this incident',
      notificationCount: notificationCounts['media-intelligence']
    },
    {
      id: 'incident-documentation',
      label: 'Incident Documentation',
      component: <IncidentDocumentationTab incidentId={incidentId} />,
      icon: '📄',
      tooltip: 'Access notes, activity logs, and crisis files in one organized location'
    },
    {
      id: 'regulatory-compliance',
      label: 'Regulatory Compliance',
      component: <RegulatoryComplianceTab incidentId={incidentId} isActive={activeTab === 'regulatory-compliance'} />,
      icon: '⚖️',
      tooltip: 'View regulatory triggers and compliance requirements',
      notificationCount: notificationCounts['regulatory-compliance']
    },
  ];

  // Check if incident is resolved (case-insensitive) 
  const isIncidentResolved = incident?.status && 
    (incident.status.toLowerCase() === 'resolved' || 
     incident.status.toLowerCase() === 'closed' ||
     incident.status.toLowerCase() === 'complete' ||
     incident.status.toLowerCase() === 'completed');

  // Add Lessons Learned tab if incident is resolved
  if (isIncidentResolved) {
    baseTabs.push({
      id: 'lessons-learned',
      label: 'Lessons Learned',
      component: <LessonsLearnedTab incidentId={incidentId} incident={incident} />,
      icon: '📚',
      tooltip: 'Document lessons learned and improvements for future incidents'
    });
  }

  return baseTabs;
  }, [incidentId, incident, notificationCounts]);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const tabItems = tabs.map((tab) => ({
    key: tab.id,
    label: (
      <Tooltip title={tab.tooltip}>
        <span style={{ 
          whiteSpace: 'nowrap', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '4px',
          width: '100%'
        }}>
          <span style={{ fontSize: '1.2em' }}>{tab.icon}</span>
          <span style={{ fontSize: '1.05em', fontWeight: '500' }}>{tab.label}</span>
          {tab.notificationCount > 0 && (
            <Badge 
              count={tab.notificationCount} 
              size="small"
              style={{ 
                backgroundColor: '#ff4d4f',
                fontSize: '10px',
                minWidth: '16px',
                height: '16px',
                lineHeight: '16px'
              }}
            />
          )}
        </span>
      </Tooltip>
    ),
    children: tab.component
  }));

  return (
    <div className="incident-details-tabs">
      <style>{`
        .incident-prominent-tabs .ant-tabs-tab {
          border: 2px solid #39325b !important;
          margin: 0 2px !important;
          border-radius: 6px !important;
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
          box-shadow: 0 2px 4px rgba(57, 50, 91, 0.1) !important;
          transition: all 0.3s ease !important;
          min-width: 120px !important;
          text-align: center !important;
          padding: 5px 11px !important;
          height: auto !important;
          line-height: 1.15 !important;
        }
        
        .incident-prominent-tabs .ant-tabs-tab:hover {
          border-color: #39325b !important;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
          box-shadow: 0 4px 8px rgba(57, 50, 91, 0.2) !important;
          transform: translateY(-1px) !important;
        }
        
        .incident-prominent-tabs .ant-tabs-tab-active {
          border: 3px solid #39325b !important;
          background: linear-gradient(135deg, #39325b 0%, #4a3f6b 100%) !important;
          color: white !important;
          font-weight: 700 !important;
          box-shadow: 0 6px 12px rgba(57, 50, 91, 0.3) !important;
        }
        
        .incident-prominent-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: white !important;
        }
        
        .incident-prominent-tabs .ant-tabs-tab-active .ant-tabs-tab-btn span {
          color: white !important;
        }
        
        .incident-prominent-tabs .ant-tabs-content-holder {
          border: 2px solid #d9d9d9;
          border-radius: 6px;
          background: white;
          padding: 13px;
          margin-top: 5px;
        }
        
        .incident-prominent-tabs .ant-tabs-nav {
          margin-bottom: 0 !important;
        }
        
        .incident-prominent-tabs .ant-tabs-nav-wrap {
          padding: 5px 4px !important;
        }
        
        .incident-prominent-tabs .ant-tabs-tab-btn {
          font-size: 12px !important;
          font-weight: 600 !important;
          padding: 2px 4px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 100% !important;
        }
        
        /* Responsive adjustments for many tabs */
        @media (max-width: 1200px) {
          .incident-prominent-tabs .ant-tabs-tab {
            min-width: 100px !important;
            margin: 0 1px !important;
            padding: 4px 9px !important;
          }
        }
        
        @media (max-width: 768px) {
          .incident-prominent-tabs .ant-tabs-tab {
            min-width: 80px !important;
            margin: 0 1px !important;
            padding: 3px 7px !important;
          }
          .incident-prominent-tabs .ant-tabs-tab-btn {
            font-size: 11px !important;
          }
        }
      `}</style>
      
      <Card 
        className="tabs-card"
        style={{ 
          marginBottom: 0,
          paddingBottom: 0,
          border: '2px solid #d9d9d9',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          backgroundColor: '#f5f5f5'
        }}
      >
        <div className="card-body" style={{ paddingBottom: 0, padding: '5px' }}>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tabItems}
            className="incident-prominent-tabs"
            type="card"
            size="large"
            style={{
              backgroundColor: '#f5f5f5',
              borderRadius: '8px'
            }}
            tabBarStyle={{
              marginBottom: '5px',
              border: 'none'
            }}
          />
        </div>
      </Card>


    </div>
  );
};

export default IncidentDetailsTabs; 